// Pure JS (no JSX imports) so it runs under `node --test`.

// Source names -> one canonical country, so Holland/Netherlands and England/United Kingdom merge in the filter.
const COUNTRY_ALIAS = {
  HOLLAND: 'NETHERLANDS',
  ENGLAND: 'UNITED KINGDOM',
  UK: 'UNITED KINGDOM',
  'U.K': 'UNITED KINGDOM',
  'U.K.': 'UNITED KINGDOM',
  UNITED_KINGDOM: 'UNITED KINGDOM',
  US: 'USA',
  'U.S': 'USA',
  'U.S.': 'USA',
  'UNITED STATES': 'USA',
  UNITED_STATES: 'USA',
};

/** Match key: trimmed + upper-cased, so PERU / Peru / " peru " all compare equal. */
export const countryKey = (v) => {
  const k = String(v ?? '').trim().toUpperCase();
  return COUNTRY_ALIAS[k] ?? k;
};

/**
 * Display label: INITCAP, except a single short all-letter name (USA, UK, UAE)
 * stays upper-case because INITCAP('USA') would read 'Usa'.
 */
export const countryLabel = (v) => {
  const s = String(v ?? '').trim();
  if (/^[A-Za-z]{1,3}$/.test(s)) return s.toUpperCase();
  return s
    .toLowerCase()
    .replace(/(^|[\s\-/])([a-zÀ-ɏ])/g, (_, sep, c) => sep + c.toUpperCase());
};

export const shipmentOriginKey = (s) => countryKey(countryKey(s?.origin_country) || s?.origin);
export const shipmentDestKey = (s) => countryKey(s?.destination_country);

const stopList = (s) => {
  let v = s?.voyage_stops;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return [];
    }
  }
  return Array.isArray(v) ? v : [];
};

const splitKeys = (v) => String(v ?? '').split(',').map(countryKey).filter(Boolean);

export const shipmentDestKeys = (s) =>
  [
    ...new Set([
      ...stopList(s).flatMap((st) => splitKeys(st?.destination_country)),
      shipmentDestKey(s),
    ]),
  ].filter(Boolean);

export const shipmentOriginKeys = (s) =>
  [
    ...new Set([
      ...stopList(s).flatMap((st) => splitKeys(st?.origin_country)),
      shipmentOriginKey(s),
    ]),
  ].filter(Boolean);

const commodityIn = (v, commodity) =>
  String(v ?? '').split(',').some((c) => c.trim().toLowerCase() === String(commodity).trim().toLowerCase());

/**
 * Dropdown option keys of one card for a selected commodity. A card is a whole voyage: its stops
 * carry the destination/origin of the member row that produced them and per-head commodities, so
 * only stops with a matching head count. Cards without stops fall back to the card-level key.
 */
const optionKeysFor = (s, commodity, stopField, cardKeys) => {
  if (!commodity) return cardKeys(s);
  const stops = stopList(s);
  if (!stops.length) return commodityIn(s?.commodities, commodity) ? cardKeys(s) : [];
  const keys = new Set();
  for (const st of stops) {
    const matches = Array.isArray(st?.heads) && st.heads.length
      ? st.heads.some((h) => commodityIn(h?.commodities, commodity))
      : commodityIn(st?.commodities ?? s?.commodities, commodity);
    if (matches) splitKeys(st?.[stopField]).forEach((k) => keys.add(k));
  }
  return [...keys];
};

export const shipmentDestOptionKeys = (s, commodity) =>
  optionKeysFor(s, commodity, 'destination_country', shipmentDestKeys);
export const shipmentOriginOptionKeys = (s, commodity) =>
  optionKeysFor(s, commodity, 'origin_country', shipmentOriginKeys);

/**
 * Parse a raw `origin` / `dest` URL value (null when the param is absent).
 * `defaultKeys` is what an absent param means; null/undefined = unrestricted.
 * Returns { mode: 'all' | 'some' | 'none', keys: Set }.
 */
export const parseCountryParam = (raw, defaultKeys = null) => {
  if (raw == null) {
    return defaultKeys
      ? { mode: 'some', keys: new Set(defaultKeys.map(countryKey)) }
      : { mode: 'all', keys: new Set() };
  }
  const v = raw.trim();
  if (v.toLowerCase() === 'all') return { mode: 'all', keys: new Set() };
  const keys = new Set(v.split(',').map(countryKey).filter(Boolean));
  if (v.toLowerCase() === 'none' || keys.size === 0) return { mode: 'none', keys: new Set() };
  return { mode: 'some', keys };
};

/**
 * Inverse of parseCountryParam: the URL value for a selection, or null to
 * delete the param. Empty selection is 'none' (not "all", unlike `status`).
 */
export const serializeCountryParam = (selectedKeys, allKeys, defaultKeys = null) => {
  const sel = [...new Set(selectedKeys.map(countryKey))].sort();
  if (sel.length === 0) return 'none';
  // All-options is checked before default-equality so a selection equal to every
  // option still counts as All.
  if (allKeys.length > 0 && allKeys.every((k) => sel.includes(k))) {
    return defaultKeys ? 'all' : null;
  }
  if (defaultKeys) {
    const def = [...new Set(defaultKeys.map(countryKey))].sort();
    if (sel.length === def.length && sel.every((k, i) => k === def[i])) return null;
  }
  return sel.join(',');
};

/**
 * URL value for the explicit "All" action, independent of any loaded option
 * list: null (delete the param) when absent already means unrestricted, else 'all'.
 */
export const allCountryParam = (defaultKeys = null) => (defaultKeys ? 'all' : null);

/** Trigger text: 'all' only for mode all, 'none' only for mode none, else first 2 names (+N). */
export const countrySummary = (selectedKeys, mode) => {
  if (mode === 'all') return { kind: 'all' };
  if (mode === 'none') return { kind: 'none' };
  const n = selectedKeys.length;
  const names = selectedKeys.slice(0, 2).map(countryLabel).join(', ') + (n > 2 ? ` +${n - 2}` : '');
  return { kind: 'names', names };
};

const passesAxis = (keys, sel) => {
  if (sel.mode === 'all') return true;
  if (sel.mode === 'none') return false;
  return keys.some((k) => sel.keys.has(k));
};

/** True when the shipment survives both the origin and destination selections. */
export const passesCountryFilter = (shipment, originSel, destSel) =>
  passesAxis(shipmentOriginKeys(shipment), originSel)
  && passesAxis(shipmentDestKeys(shipment), destSel);

/**
 * Disabled state of a checklist dropdown's All / Clear actions. `allActive`
 * (the filter mode flag) wins over the selected count, so an unrestricted
 * filter with zero loaded options still offers Clear.
 */
export const checklistActionState = ({ allActive, selectedCount, itemCount }) => {
  const allSelected = allActive ?? selectedCount === itemCount;
  const noneSelected = allActive === true ? false : selectedCount === 0;
  return { allSelected, noneSelected };
};
